import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Form, Input, Modal, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { CURRENCY_CODES, SUGGESTED_CURRENCIES } from '@/shared/lib/currencies'
import { currencyName, getDisplayTimeZone } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { TENANT_LOCALES, timeZoneOptions } from './timeZones'
import {
  BUSINESS_KINDS,
  MAX_CODE_LENGTH,
  createTenant,
  updateTenant,
  validateTenantCode,
  type TenantEntry,
  type TenantProvisionResult,
} from './tenantsApi'

interface FormValues {
  code: string
  name: string
  businessKind: 'ISP' | 'VENUE'
  currency: string
  dayTimezone: string
  locale: string
  adminUsername: string
  adminDisplayName: string
  adminPassword: string
}

interface Props {
  open: boolean
  /** `null` = yangi tashkilot */
  tenant: TenantEntry | null
  onClose: () => void
  /** Yaratilgandan keyin bir martalik parolni ko'rsatish uchun. */
  onProvisioned: (result: TenantProvisionResult) => void
}

/**
 * Tashkilot yaratish / tahrirlash oynasi.
 *
 * <h2>⚠️ Yaratish va tahrirlash maydonlari BOSHQA</h2>
 * Tahrirlashda `code`, `businessKind` va `currency` YOPIQ:
 * <ul>
 *   <li>`code` — undan RADIUS realm'i hosil bo'lgan va minglab abonent
 *       allaqachon `login@realm` bilan sozlangan bo'lishi mumkin;</li>
 *   <li>`currency` — uni almashtirish hech narsani konvertatsiya
 *       qilmaydi (`docs/02-LEDGER.md` §11.2). Buning uchun ALOHIDA yo'l
 *       bor: Sozlamalar sahifasi, moliyaviy izi bor tashkilotni rad
 *       etadigan qorovul bilan;</li>
 *   <li>`businessKind` — u butun panel tuzilishini belgilaydi.</li>
 * </ul>
 *
 * <h2>⚠️ Parol maydoni BO'SH QOLDIRILISHI mumkin va bu TAVSIYA ETILADI</h2>
 * Bo'sh bo'lsa server kuchli tasodifiy parol yaratadi va uni javobda
 * BIR MARTA qaytaradi. Operator o'ylab topgan parol deyarli har doim
 * kuchsizroq bo'ladi.
 */
export function TenantFormModal({ open, tenant, onClose, onProvisioned }: Props) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const [codePreview, setCodePreview] = useState('')
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const editing = tenant !== null

  const currentCurrency = user?.tenant.currency ?? null
  const currencyOptions = useMemo(
    () =>
      Array.from(new Set([...SUGGESTED_CURRENCIES, ...CURRENCY_CODES])).map((code) => ({
        value: code,
        label: `${code} · ${currencyName(code)}`,
      })),
    [],
  )
  const zoneOptions = useMemo(timeZoneOptions, [])
  const localeOptions = TENANT_LOCALES.map((code) => ({ value: code, label: t(`language.${code}`) }))

  useEffect(() => {
    if (!open) return
    if (tenant) {
      form.setFieldsValue({
        code: tenant.code,
        name: tenant.name,
        businessKind: tenant.businessKind,
        currency: tenant.currency,
        dayTimezone: tenant.dayTimezone,
        locale: tenant.locale,
      })
      // ⚠️ Realm endi ixtiyoriy (`V202608062800`) — mavjud bo’lsa
      // ko’rsatiladi, bo’lmasa oynada realm haqida hech narsa
      // va’da qilinmaydi.
      setCodePreview(tenant.realm ?? "")
    } else {
      form.resetFields()
      form.setFieldsValue({
        businessKind: 'ISP',
        currency: currentCurrency ?? undefined,
        dayTimezone: getDisplayTimeZone(),
        locale: i18n.language,
      })
      setCodePreview('')
    }
  }, [open, tenant, form, currentCurrency, i18n.language])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (tenant) {
        await updateTenant(tenant.id, {
          name: values.name.trim(),
          dayTimezone: values.dayTimezone,
          locale: values.locale,
        })
        return null
      }
      return createTenant({
        code: values.code.trim(),
        name: values.name.trim(),
        businessKind: values.businessKind,
        currency: values.currency,
        dayTimezone: values.dayTimezone,
        locale: values.locale,
        adminUsername: values.adminUsername.trim(),
        adminDisplayName: values.adminDisplayName?.trim() || undefined,
        adminPassword: values.adminPassword?.trim() || undefined,
      })
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all })
      // ⚠️ Yangi tashkilot joriy foydalanuvchining a'zoliklarini
      // O'ZGARTIRMAYDI (u yerda a'zo emas), lekin tanlagich ro'yxati
      // baribir yangilansin: keyin unga a'zolik qo'shilishi mumkin.
      void queryClient.invalidateQueries({ queryKey: queryKeys.myTenants })
      form.resetFields()
      onClose()
      if (result) onProvisioned(result)
    },
  })

  // ⚠️ `await form.validateFields()` YOZILMAYDI: forma yaroqsiz bo'lsa u
  //    rad javob beradi va `onOk={() => void submit()}` uni HECH QAYERDA
  //    ushlamasdi — N6 dagi ushlanmagan `pageerror` ning aynan shu ikkinchi
  //    ko'rinishi (`SiteFormModal` da ham shunday edi).
  const submit = () => submitOnValid(form, (values) => save.mutate(values))

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={editing ? t('tenants.editTitle') : t('tenants.createTitle')}
      onOk={() => void submit()}
      onCancel={close}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={680}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={editing ? t('tenants.editNote') : t('tenants.formNote')}
      />
      <Form form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="code"
          label={t('tenants.code')}
          extra={
            editing
              ? t('tenants.codeLocked')
              : codePreview
                ? t('tenants.realmPreview', { realm: codePreview })
                : t('tenants.codeHint')
          }
          rules={[
            {
              validator: (_rule, value: string) => {
                if (editing) return Promise.resolve()
                const error = validateTenantCode(value ?? '')
                if (!error) return Promise.resolve()
                return Promise.reject(
                  new Error(t(`tenants.validation.code.${error}`, { max: MAX_CODE_LENGTH })),
                )
              },
            },
          ]}
        >
          <Input
            disabled={editing}
            placeholder="kabul-net"
            maxLength={MAX_CODE_LENGTH}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('tenants.name')}
          rules={[{ required: true, message: t('tenants.validation.nameRequired') }]}
        >
          <Input placeholder={t('tenants.namePlaceholder')} maxLength={128} />
        </Form.Item>

        <Form.Item
          name="businessKind"
          label={t('tenants.businessKind')}
          extra={editing ? t('tenants.businessKindLocked') : t('tenants.businessKindHint')}
        >
          <Select
            disabled={editing}
            options={BUSINESS_KINDS.map((k) => ({ value: k, label: t(`businessKind.${k}`) }))}
          />
        </Form.Item>

        <Form.Item
          name="currency"
          label={t('tenants.currency')}
          extra={editing ? t('tenants.currencyLocked') : t('tenants.currencyHint')}
          rules={[{ required: !editing, message: t('tenants.validation.currency') }]}
        >
          <Select
            showSearch
            disabled={editing}
            options={currencyOptions}
            optionFilterProp="label"
            placeholder={t('tenants.currencyPlaceholder')}
          />
        </Form.Item>

        <Form.Item
          name="dayTimezone"
          label={t('tenants.dayTimezone')}
          extra={t('tenants.dayTimezoneHint')}
          rules={[{ required: true, message: t('tenants.validation.dayTimezone') }]}
        >
          <Select showSearch options={zoneOptions} optionFilterProp="label" />
        </Form.Item>

        <Form.Item name="locale" label={t('tenants.locale')} rules={[{ required: true }]}>
          <Select options={localeOptions} />
        </Form.Item>

        {editing ? null : (
          <>
            <Typography.Title level={5} style={{ marginTop: 8 }}>
              {t('tenants.firstAdmin')}
            </Typography.Title>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('tenants.firstAdminNote')}
            />
            <Form.Item
              name="adminUsername"
              label={t('tenants.adminUsername')}
              extra={t('tenants.adminUsernameHint')}
              rules={[
                { required: true, message: t('tenants.validation.adminUsernameRequired') },
                {
                  pattern: /^[a-zA-Z0-9._@-]{3,64}$/,
                  message: t('tenants.validation.adminUsernameShape'),
                },
              ]}
            >
              <Input placeholder="admin-kabul" maxLength={64} />
            </Form.Item>
            <Form.Item name="adminDisplayName" label={t('tenants.adminDisplayName')}>
              <Input maxLength={128} />
            </Form.Item>
            <Form.Item
              name="adminPassword"
              label={t('tenants.adminPassword')}
              extra={t('tenants.adminPasswordHint')}
              rules={[{ min: 12, message: t('tenants.validation.adminPasswordShort') }]}
            >
              <Input.Password autoComplete="new-password" maxLength={128} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}
